/* eslint-disable no-underscore-dangle */
/* eslint-disable no-plusplus */
import React, { Fragment, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Placement } from '@floating-ui/react';

import { Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight } from '@fortawesome/free-solid-svg-icons';
import BezierCurve from './BezierCurve';
import Vector from './Vector';
import { TimelineStage } from '../../../../services/types/Timeline';
import MilestoneInfoContainer from './MilestoneInfoContainer';

interface Props {
  stages: TimelineStage[];
  hidenMilestones: number;
  saveDate: (milestoneId: number, date: Date | null) => void;
  onWidthset: (width: number) => void;
}

const Graphic = (props: Props) => {
  const { stages, hidenMilestones, saveDate, onWidthset } = props;
  const { t } = useTranslation();
  const [milestoneCalendarOpened, setMilestoneCalendarOpened] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const getMaxScroll = () => {
    if (timelineRef.current && containerRef.current) {
      return timelineRef.current.clientWidth - containerRef.current.clientWidth;
    }
    return 0;
  };
  const scrollTo = (direction: 'left' | 'right') => {
    if (containerRef.current) {
      const maxScroll = getMaxScroll();
      let offset = scrollPosition + (direction === 'left' ? -1 : 1) * 500;
      if (offset > maxScroll) {
        offset = maxScroll;
      }
      if (offset < 0) {
        offset = 0;
      }
      containerRef.current.scrollTo({
        left: offset,
        behavior: 'smooth',
      });
      setScrollPosition(offset);
    }
  };

  useEffect(() => {
    const maxScroll = getMaxScroll();
    if (containerRef.current && timelineRef.current) {
      containerRef.current.scrollTo({
        left: maxScroll,
        behavior: 'smooth',
      });
      setScrollPosition(maxScroll);
    }
  }, []);

  let milestonesSeparationQty = 0;
  let excededCycles = 0;
  const minTimelineWidth = 1260;
  const stageWidths: number[] = [];

  let maxCycles = 16;
  let finalWidth = minTimelineWidth;

  (() => {
    const executionStage = stages.find((x) => x.name === 'Execution');
    if (executionStage && executionStage.milestones.length <= 3 && hidenMilestones === 0) {
      maxCycles = 15;
    }

    stages.forEach((x) => {
      milestonesSeparationQty += x.milestones.length;

      excededCycles += x.milestones.length;
    });
    milestonesSeparationQty += 1;

    excededCycles =
      excededCycles > maxCycles ? Math.floor((excededCycles - maxCycles - hidenMilestones) / 2) : 0;

    const calculatedWidth = 1080 + excededCycles * 180;
    finalWidth = Math.max(minTimelineWidth, calculatedWidth); // Asegura que el ancho no sea menor al mínimo
  })();

  // onWidthset() actualiza el estado de OperationHome (un componente
  // ancestro, no éste) - llamarlo directo en el cuerpo del render, como
  // estaba antes, es actualizar un componente distinto mientras éste se
  // está renderizando. Mismo bug (y mismo fix) que notifyError() en
  // OperationHome/index.tsx (Fase 4, QA manual): React 19 lo corta con
  // "Cannot update a component while rendering a different component".
  useEffect(() => {
    onWidthset(finalWidth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalWidth]);

  const xMilestoneSeparation = (1080 + excededCycles * 180) / milestonesSeparationQty;

  (() => {
    stages.forEach((stage, i) => {
      let stageWidth = stage.milestones.length * xMilestoneSeparation;
      if (i === 0 && i !== stages.length - 1) {
        stageWidth -= xMilestoneSeparation / 4;
      } else if (i === stages.length - 1) {
        stageWidth += xMilestoneSeparation * 1.25;
      }
      stageWidths.push(stageWidth);
    });
  })();

  const renderMilestones = () => {
    let xMilestone = xMilestoneSeparation / 2;
    let xStage = 0;
    let xToday = 0;
    let toggleMilestoneDirection = true;
    let milestonesCount = 0;
    return stages.map((stage, stageIndex) => {
      if (stageIndex > 0) {
        xStage += stageWidths[stageIndex - 1];
      }
      const indexPassed = stage.milestones.findIndex((m) => m.lastPassed);
      if (stage.current) {
        if (indexPassed >= 0) {
          xToday += (milestonesCount + indexPassed + 1) * xMilestoneSeparation;
        } else {
          xToday += xStage + xMilestoneSeparation / 5;
        }
      }

      const todayPoint = xToday ? new BezierCurve(6 + excededCycles).GetPoint(xToday) : null;

      milestonesCount += stage.milestones.length;

      return stage.milestones.map((x, i) => {
        if (!(stageIndex === 0 && i === 0)) {
          xMilestone += xMilestoneSeparation;
        }

        const point = new BezierCurve(6 + excededCycles).GetPoint(xMilestone);
        toggleMilestoneDirection = !toggleMilestoneDirection;
        let popDirection: Placement = toggleMilestoneDirection ? 'bottom-start' : 'top-start';
        if (stageIndex + 1 === stages.length) {
          popDirection = toggleMilestoneDirection ? 'bottom-end' : 'top-end';
        }

        const drawToday = stage.current && (indexPassed === i || (indexPassed === -1 && i === 0));

        return (
          <Fragment key={`milestone${stageIndex.toString()}_${i.toString()}`}>
            <div
              className={`milestone-circle ${
                stage.current && x.status === 'COMPLETED' ? 'completed' : ''
              }`}
              style={{ top: `${point._y}px`, left: `${point._x}px` }}
            />
            <MilestoneInfoContainer
              milestone={x}
              toggleMilestoneDirection={toggleMilestoneDirection}
              milestoneCalendarOpenedId={milestoneCalendarOpened}
              coords={point}
              xMilestoneSeparation={xMilestoneSeparation}
              isLast={stage.milestones.length === i + 1 && stages.length === stageIndex + 1}
              calendarDirection={popDirection}
              setMilestoneCalendarOpened={setMilestoneCalendarOpened}
              saveDate={saveDate}
            />

            {drawToday && todayPoint && (
              <>
                <div
                  className="today-circle"
                  style={{ top: `${todayPoint._y}px`, left: `${todayPoint._x}px` }}
                />
                <div
                  className="passed-stage-section"
                  style={{
                    left: `${xStage + 2}px`,
                    top: `${-107}px`,
                    height: `${107 + todayPoint._y}px`,
                    width: `${xToday - xStage - 1}px`,
                  }}
                />
                <div
                  className="passed-stage-section down"
                  style={{
                    left: `${xStage + 2}px`,
                    top: `${todayPoint._y}px`,
                    height: `${95 + (170 - todayPoint._y) - 2}px`,
                    width: `${xToday - xStage - 1}px`,
                  }}
                />
                <div className="today-badge" style={{ left: `${todayPoint._x}px` }}>
                  <span>{xToday < xMilestoneSeparation / 2 ? t('todayShort') : t('today')}</span>
                </div>
              </>
            )}
          </Fragment>
        );
      });
    });
  };

  const bluePassed = '#0099D6'; // '#62C6EE';
  const blueAfter = '#0099D6';
  const drawGraph = () => {
    let xStage = 0;
    let milestonesCount = 0;
    let xToday = 0;
    return stages.map((x, i) => {
      if (i > 0) {
        xStage += stageWidths[i - 1];
      }
      const indexPassed = x.milestones.findIndex((m) => m.lastPassed);
      if (x.current) {
        if (indexPassed >= 0) {
          xToday += (milestonesCount + indexPassed + 1) * xMilestoneSeparation;
        } else {
          xToday += xStage + xMilestoneSeparation / 4;
        }
      }
      milestonesCount += x.milestones.length;
      if (x.current) {
        return (
          <Fragment key={`vectorSplitted${i.toString()}`}>
            <Vector
              xStart={xStage}
              width={xToday - xStage}
              color={bluePassed}
              drawCycleCount={3 + Math.ceil(excededCycles / 2)}
            />
            <Vector
              xStart={xToday}
              // Sólo puede dar negativo para la primera etapa (i === 0): su
              // ancho ya viene achicado en xMilestoneSeparation/4 (ver el
              // cálculo de stageWidths más arriba), achique que la fórmula de
              // xToday no contempla cuando todos sus milestones ya pasaron -
              // un <svg> no acepta width/viewBox negativos, el navegador lo
              // ignora en silencio (Fase 4, hallazgo documentado). No queda
              // nada real por dibujar "después de hoy" en ese caso.
              width={Math.max(0, xStage + stageWidths[i] - xToday)}
              color={blueAfter}
              drawCycleCount={3 + Math.ceil(excededCycles / 2)}
            />
          </Fragment>
        );
      }
      return (
        <Vector
          key={`vector${i.toString()}`}
          xStart={xStage}
          width={stageWidths[i]}
          color="#E3E7EC"
          drawCycleCount={3 + Math.ceil(excededCycles / 2)}
        />
      );
    });
  };
  const renderStageHeaders = () => (
    <>
      {stages.map((x, i) => {
        const stageWidth = stageWidths[i];
        return (
          <div
            key={`stageHeader${i.toString()}`}
            className={`stage-header ${x.current ? 'current' : ''}`}
            style={{ width: `${stageWidth}px` }}
          >
            {x.name}
          </div>
        );
      })}
    </>
  );

  const renderStages = () => (
    <>
      {stages.map((x, i) => {
        const stageWidth = stageWidths[i];
        return (
          <div
            key={`stage${i.toString()}`}
            className={`stage ${x.current ? 'current' : ''}`}
            style={{ width: `${stageWidth}px` }}
          />
        );
      })}
    </>
  );
  return (
    <div className="timeline-container">
      <div className="scroll-button-container">
        {timelineRef.current &&
          containerRef.current &&
          timelineRef.current.clientWidth > containerRef.current.clientWidth && (
            <Button
              id="left-scroll"
              className="btn-tertiary scroll-button"
              onClick={() => {
                scrollTo('left');
              }}
              color="nothing"
              disabled={scrollPosition <= 0}
              type="button"
              active={false}
            >
              <div className="d-flex align-items-center justify-content-center">
                <FontAwesomeIcon icon={faAngleLeft} />
              </div>
            </Button>
          )}
      </div>
      <div className="table-responsive " style={{ height: '500px' }} ref={containerRef}>
        <div
          className="timeline-wrapper"
          style={{ width: `${1080 + excededCycles * 180}px` }}
          ref={timelineRef}
        >
          <div className="stage-headers" style={{ width: `${1080 + excededCycles * 180}px` }}>
            {renderStageHeaders()}
          </div>
          <div className="timeline" style={{ width: `${1080 + excededCycles * 180}px` }}>
            <div className="stages" style={{ width: `${1080 + excededCycles * 180}px` }}>
              {renderStages()}
            </div>
            <div className="snake">{drawGraph()}</div>
            <div className="milestones" style={{ width: `${1080 + excededCycles * 180}px` }}>
              {renderMilestones()}
            </div>
          </div>
        </div>
      </div>
      <div className="scroll-button-container">
        {timelineRef.current &&
          containerRef.current &&
          timelineRef.current.clientWidth > containerRef.current.clientWidth && (
            <Button
              id="right-scroll"
              className="btn-tertiary scroll-button"
              onClick={() => {
                scrollTo('right');
              }}
              color="nothing"
              disabled={scrollPosition >= getMaxScroll()}
              type="button"
              active={false}
            >
              <div className="d-flex align-items-center justify-content-center">
                <FontAwesomeIcon icon={faAngleRight} />
              </div>
            </Button>
          )}
      </div>
    </div>
  );
};

export default Graphic;
